import { useMemo } from 'react';

import {
  ModuleTagging,
  ModuleWithDirections,
  useModuleTagging,
  useModules,
} from '@suntex.fim/framework';
import { buildModuleEnabledPredicate } from '@suntex.fim/hub';

import { useModuleAccessFilters } from './useModuleAccessFilters';

export interface ModuleAccessResult {
  isModuleEnabled: (moduleSlug: string) => boolean;
  enabledModules: ModuleWithDirections[] | undefined;
  moduleTagging: readonly ModuleTagging[] | undefined;
  isLoading: boolean;
}

/**
 * Single source of truth for whether a module is accessible to the current
 * player, combining hub focus filters, school-level access filters, and
 * module tagging. Fails closed while any of its dependencies are loading.
 */
export function useModuleAccess(): ModuleAccessResult {
  const {
    enable,
    disable,
    isLoading: filtersLoading,
  } = useModuleAccessFilters();
  const { moduleTagging, isLoading: taggingLoading } = useModuleTagging();
  const modules = useModules();

  const isLoading =
    filtersLoading || taggingLoading || !moduleTagging || !modules;

  const isModuleEnabled = useMemo(
    () =>
      isLoading
        ? () => false
        : buildModuleEnabledPredicate({ moduleTagging, enable, disable }),
    [isLoading, moduleTagging, enable, disable],
  );

  const enabledModules = useMemo(
    () =>
      isLoading ? undefined : modules.filter((m) => isModuleEnabled(m.slug)),
    [isLoading, modules, isModuleEnabled],
  );

  return { isModuleEnabled, enabledModules, moduleTagging, isLoading };
}
